import type { Localized } from '@/i18n'

/**
 * Science programme catalogue.
 *
 * These are established total-eclipse observation activities, listed so the
 * team can scope equipment and training. Every protocol is still `draft`:
 * the step-by-step field procedure is published separately and only then does
 * `protocolStatus` become `published`.
 */

export type Difficulty = 'school' | 'community' | 'research'

export interface Experiment {
  slug: string
  title: Localized
  summary: Localized
  difficulty: Difficulty
  /** Short equipment list, shown as chips. */
  equipment: Localized<string[]>
  protocolStatus: 'draft' | 'published'
}

export const EXPERIMENTS: Experiment[] = [
  {
    slug: 'temperature-drop',
    title: {
      id: 'Penurunan suhu dan kelembapan',
      en: 'Temperature and humidity drop',
    },
    summary: {
      id: 'Merekam perubahan suhu udara, kelembapan, dan tekanan sejak kontak pertama hingga kontak keempat untuk memetakan respons atmosfer terhadap hilangnya radiasi Matahari.',
      en: 'Logging air temperature, humidity, and pressure from first to fourth contact to map the atmospheric response to the loss of solar radiation.',
    },
    difficulty: 'school',
    equipment: {
      id: ['Termometer digital', 'Higrometer', 'Pencatat waktu tersinkron', 'Lembar data'],
      en: ['Digital thermometer', 'Hygrometer', 'Synchronised timer', 'Data sheet'],
    },
    protocolStatus: 'draft',
  },
  {
    slug: 'shadow-bands',
    title: {
      id: 'Pita bayangan (shadow bands)',
      en: 'Shadow bands',
    },
    summary: {
      id: 'Mengamati dan merekam pita terang-gelap yang bergerak di permukaan putih beberapa detik sebelum dan sesudah totalitas — fenomena turbulensi atmosfer yang masih jarang terdokumentasi dengan baik.',
      en: 'Observing and filming the faint moving light-and-dark bands that cross a white surface seconds before and after totality — an atmospheric turbulence effect that remains poorly documented.',
    },
    difficulty: 'community',
    equipment: {
      id: ['Kain putih 2×2 m', 'Kamera video ≥60 fps', 'Penanda skala', 'Perekam waktu GPS'],
      en: ['2×2 m white sheet', 'Video camera ≥60 fps', 'Scale markers', 'GPS time source'],
    },
    protocolStatus: 'draft',
  },
  {
    slug: 'baily-beads',
    title: {
      id: 'Manik Baily dan pengukuran diameter Matahari',
      en: "Baily's beads and solar diameter",
    },
    summary: {
      id: 'Merekam waktu kemunculan manik Baily di tepi bulan untuk menghitung diameter Matahari secara presisi — replikasi metode yang dipakai dalam pemantauan jangka panjang aktivitas Matahari.',
      en: "Timing the appearance of Baily's beads along the lunar limb to derive a precise solar diameter — a replication of the method used in long-term solar monitoring.",
    },
    difficulty: 'research',
    equipment: {
      id: ['Teleskop dengan filter Matahari', 'Kamera berkecepatan tinggi', 'Penanda waktu GPS'],
      en: ['Telescope with solar filter', 'High-speed camera', 'GPS time stamping'],
    },
    protocolStatus: 'draft',
  },
  {
    slug: 'corona-imaging',
    title: {
      id: 'Pencitraan korona Matahari',
      en: 'Solar corona imaging',
    },
    summary: {
      id: 'Mengambil seri eksposur bertingkat selama totalitas untuk menyusun citra korona rentang dinamis tinggi dan memetakan struktur medan magnet Matahari.',
      en: 'Capturing a bracketed exposure series through totality to assemble a high-dynamic-range image of the corona and trace the solar magnetic field structure.',
    },
    difficulty: 'research',
    equipment: {
      id: ['Kamera mirrorless/DSLR', 'Lensa tele ≥300 mm', 'Tripod kokoh', 'Intervalometer'],
      en: ['Mirrorless/DSLR camera', 'Telephoto lens ≥300 mm', 'Sturdy tripod', 'Intervalometer'],
    },
    protocolStatus: 'draft',
  },
  {
    slug: 'pinhole-projection',
    title: {
      id: 'Proyeksi lubang jarum',
      en: 'Pinhole projection',
    },
    summary: {
      id: 'Eksperimen paling aman dan paling mudah direplikasi sekolah: memproyeksikan sabit Matahari melalui lubang jarum, saringan, atau celah dedaunan, lalu mendokumentasikan perubahan bentuknya.',
      en: 'The safest and most easily reproduced school activity: projecting the solar crescent through a pinhole, a colander, or gaps in foliage, and documenting how the shape changes.',
    },
    difficulty: 'school',
    equipment: {
      id: ['Karton', 'Jarum', 'Layar putih', 'Kamera ponsel'],
      en: ['Card stock', 'Pin', 'White screen', 'Phone camera'],
    },
    protocolStatus: 'draft',
  },
  {
    slug: 'radio-propagation',
    title: {
      id: 'Perubahan propagasi radio ionosfer',
      en: 'Ionospheric radio propagation',
    },
    summary: {
      id: 'Memantau kekuatan sinyal VLF/HF selama gerhana untuk mengukur respons lapisan D ionosfer ketika bayangan Bulan memutus ionisasi ultraviolet.',
      en: 'Monitoring VLF/HF signal strength through the eclipse to measure how the ionospheric D layer responds when the Moon’s shadow cuts off ultraviolet ionisation.',
    },
    difficulty: 'research',
    equipment: {
      id: ['Penerima SDR', 'Antena loop/kawat', 'Pencatat data', 'Referensi waktu GPS'],
      en: ['SDR receiver', 'Loop/wire antenna', 'Data logger', 'GPS time reference'],
    },
    protocolStatus: 'draft',
  },
  {
    slug: 'fauna-behaviour',
    title: {
      id: 'Perubahan perilaku fauna dan lingkungan',
      en: 'Fauna and environmental response',
    },
    summary: {
      id: 'Survei terstruktur atas respons burung, serangga, dan ternak terhadap senja mendadak, dipadukan dengan pengukuran intensitas cahaya.',
      en: 'A structured survey of how birds, insects, and livestock respond to the sudden twilight, paired with light-intensity measurements.',
    },
    difficulty: 'community',
    equipment: {
      id: ['Perekam suara', 'Lux meter', 'Lembar observasi terstruktur'],
      en: ['Audio recorder', 'Lux meter', 'Structured observation sheet'],
    },
    protocolStatus: 'draft',
  },
]
